import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    prepareMarketplaceListing,
    publishMarketplaceListing,
    createMarketplaceLocation,
    getInventoryListings,
} from "../../../store/actions/marketplaceListing.action";

// Condition options match _mapCondition in walmart-service/ebay.adapter.js.
// Keep these labels consistent with the eBay enum we forward.
const CONDITION_OPTIONS = [
    { value: "New", label: "New" },
    { value: "New Other", label: "New Other" },
    { value: "Like New", label: "Like New" },
    { value: "Manufacturer Refurbished", label: "Manufacturer Refurbished" },
    { value: "Seller Refurbished", label: "Seller Refurbished" },
    { value: "Used Excellent", label: "Used — Excellent" },
    { value: "Used Very Good", label: "Used — Very Good" },
    { value: "Used Good", label: "Used — Good" },
    { value: "Used Acceptable", label: "Used — Acceptable" },
    { value: "For Parts or Not Working", label: "For Parts or Not Working" },
];

/**
 * Helpers to bridge react-select's { value, label } object shape with the
 * adapter API which expects plain id/key strings.
 */
const toOption = (val, options) => options.find((o) => o.value === val) || null;
const optionFromIdName = (item) => (item ? { value: item.id, label: item.name } : null);
const optionFromCategory = (c) => (c ? { value: c.categoryId, label: c.categoryPath || c.categoryName } : null);
const optionFromLocation = (l) => (l ? { value: l.key, label: l.name } : null);

const EBAY_MARKETPLACE = "ebay";
// Primary: durable Seller Hub path (main www.ebay.com domain, not the Akamai-fronted
// bizpolicy.ebay.com subdomain which has had transient DNS failures). The seller
// navigates: Account Settings → Business Policies in the left nav.
const POLICY_HUB_URL = "https://www.ebay.com/sh/settings/account";
// Direct deep-link to the opt-in page on bizpolicy.ebay.com — used as a secondary
// link since it goes straight to "Opt in" but the host has been intermittent.
const POLICY_OPTIN_URL = "https://www.bizpolicy.ebay.com/businesspolicy/policyoptin";

/**
 * Render a single dynamic aspect field. eBay aspects come back with
 *   { name, type, mode, cardinality, required, values, suggestedValues }
 * and we translate that to the right input.
 */
function AspectField({ aspect, value, onChange }) {
    const isEnum = aspect.mode === "SELECTION_ONLY" && aspect.values?.length > 0;
    const isMulti = aspect.cardinality === "MULTI";
    const isNumber = aspect.type === "NUMBER";

    if (isEnum && !isMulti) {
        const options = aspect.values.map((v) => ({ value: v, label: v }));
        return (
            <Select
                options={options}
                value={value ? { value, label: value } : null}
                onChange={(opt) => onChange(opt ? opt.value : "")}
                placeholder={`-- Select ${aspect.name} --`}
                classNamePrefix="list"
                isClearable
            />
        );
    }

    if (isMulti) {
        const suggestedHint = aspect.suggestedValues?.length
            ? ` (e.g., ${aspect.suggestedValues.slice(0, 3).join(", ")})`
            : "";
        const placeholder = `Enter values separated by commas${suggestedHint}`;
        return (
            <Form.Control
                type="text"
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    return (
        <Form.Control
            type={isNumber ? "number" : "text"}
            placeholder={`Enter ${aspect.name}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

AspectField.propTypes = {
    aspect: PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.string,
        mode: PropTypes.string,
        cardinality: PropTypes.string,
        required: PropTypes.bool,
        values: PropTypes.arrayOf(PropTypes.string),
        suggestedValues: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};
AspectField.defaultProps = { value: "" };

/**
 * Map WMS WeightUnit enum value (or legacy string) to eBay's WeightUnitOfMeasure.
 * WMS enum (from WMS-API/WMS.Common/WMSEnum.cs):
 *   1=LB, 2=OZ, 3=KG, 4=GM
 * eBay accepts: POUND, OUNCE, KILOGRAM, GRAM.
 */
function mapWeightUnit(wmsValue) {
    const byNumber = { 1: "POUND", 2: "OUNCE", 3: "KILOGRAM", 4: "GRAM" };
    if (typeof wmsValue === "number" && byNumber[wmsValue]) return byNumber[wmsValue];
    const byString = { LB: "POUND", OZ: "OUNCE", KG: "KILOGRAM", GM: "GRAM", POUND: "POUND", OUNCE: "OUNCE", KILOGRAM: "KILOGRAM", GRAM: "GRAM" };
    const key = String(wmsValue || "").toUpperCase();
    return byString[key] || "POUND";
}

/**
 * Map WMS DimensionUnit enum value (or legacy string) to eBay's LengthUnitOfMeasure.
 * WMS enum: 1=INCH, 2=CMS. eBay accepts: INCH, CENTIMETER.
 */
function mapDimensionUnit(wmsValue) {
    const byNumber = { 1: "INCH", 2: "CENTIMETER" };
    if (typeof wmsValue === "number" && byNumber[wmsValue]) return byNumber[wmsValue];
    const byString = { INCH: "INCH", IN: "INCH", CM: "CENTIMETER", CMS: "CENTIMETER", CENTIMETER: "CENTIMETER" };
    const key = String(wmsValue || "").toUpperCase();
    return byString[key] || "INCH";
}

/**
 * Pick the right submit-button label for the four (submitting × existingListing) states.
 */
function publishButtonLabel(submitting, isUpdate) {
    if (submitting && isUpdate) return "Updating…";
    if (submitting) return "Publishing…";
    if (isUpdate) return "Update Listing";
    return "Publish to eBay";
}

/**
 * Convert form values back to eBay's expected `{ aspectName: [values] }` shape.
 * eBay rejects bare strings — every value is wrapped in an array even for SINGLE cardinality.
 */
function buildAspectsPayload(aspectFormState, allAspects) {
    const out = {};
    for (const aspect of allAspects) {
        const raw = aspectFormState[aspect.name];
        if (raw === undefined || raw === null || raw === "") continue;
        if (aspect.cardinality === "MULTI") {
            const arr = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
            if (arr.length) out[aspect.name] = arr;
        } else {
            out[aspect.name] = [String(raw)];
        }
    }
    return out;
}

/**
 * eBay step in the MarketplacePushWizard.
 *
 * Self-contained: loads its own prereqs on mount, manages its own form state,
 * publishes when the user clicks Publish. Reports back to the wizard via
 * onPublishComplete(result) so the wizard can mark the step status and decide
 * whether to auto-advance.
 */
export default function EbayStep({ product, marketplaceId, onPublishComplete, onSkip }) {
    const [loading, setLoading] = useState(false);
    const [prereqs, setPrereqs] = useState(null);
    const [missingPolicies, setMissingPolicies] = useState(null);
    const [policiesOptedOut, setPoliciesOptedOut] = useState(null);
    const [missingLocations, setMissingLocations] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [existingListing, setExistingListing] = useState(null);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");
    const [quantity, setQuantity] = useState("");
    const [condition, setCondition] = useState("New");
    const [categoryId, setCategoryId] = useState("");
    const [fulfillmentPolicyId, setFulfillmentPolicyId] = useState("");
    const [paymentPolicyId, setPaymentPolicyId] = useState("");
    const [returnPolicyId, setReturnPolicyId] = useState("");
    const [merchantLocationKey, setMerchantLocationKey] = useState("");
    const [aspectValues, setAspectValues] = useState({});
    const [showRecommended, setShowRecommended] = useState(false);
    const [showOptional, setShowOptional] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [publishResult, setPublishResult] = useState(null);

    // Inline location-create form state (shown when missingLocations is set)
    const [locName, setLocName] = useState("Main Warehouse");
    const [locAddressLine1, setLocAddressLine1] = useState("");
    const [locAddressLine2, setLocAddressLine2] = useState("");
    const [locCity, setLocCity] = useState("");
    const [locState, setLocState] = useState("");
    const [locPostalCode, setLocPostalCode] = useState("");
    const [locCountry, setLocCountry] = useState("US");
    const [locSubmitting, setLocSubmitting] = useState(false);

    const productId = product?.id || product?.Id;

    /**
     * Fetch eBay prereqs + existing listing. Extracted as a callable so the
     * location-create form can re-trigger it after successful registration.
     *
     * If a `cancelledRef` ({ current: boolean }) is supplied, mid-flight
     * results are dropped when current becomes true — used by useEffect's
     * cleanup to abandon stale loads on unmount. Callers that don't need
     * cancellation can omit the argument entirely.
     */
    const loadPrereqs = async (cancelledRef) => {
        const isCancelled = () => cancelledRef?.current === true;

        setLoading(true);
        setLoadError(null);
        setMissingPolicies(null);
        setPoliciesOptedOut(null);
        setMissingLocations(null);
        setPrereqs(null);
        setPublishResult(null);
        try {
            const [prepResp, existingResp] = await Promise.all([
                prepareMarketplaceListing(EBAY_MARKETPLACE, {
                    companySellerId: product.companySellerId,
                    inventoryProductId: productId,
                    marketplaceRegion: "EBAY_US",
                }),
                getInventoryListings({ inventoryProductId: productId }).catch(() => ({ data: { EntityList: [] } })),
            ]);
            if (isCancelled()) return;

            const body = prepResp?.data;
            if (body?.success === false && body?.error?.code === "EBAY_BUSINESS_POLICIES_OPTED_OUT") {
                setPoliciesOptedOut(body.error.message || "Seller has opted out of eBay business policies.");
                return;
            }
            if (body?.success === false && body?.error?.code === "MISSING_POLICIES") {
                setMissingPolicies(body.error.message || "Missing eBay business policies.");
                return;
            }
            if (body?.success === false && body?.error?.code === "MISSING_LOCATIONS") {
                setMissingLocations(body.error.message || "No eBay inventory locations.");
                return;
            }
            if (body?.success !== true) {
                setLoadError(body?.error?.message || "Failed to load eBay listing fields.");
                return;
            }

            const data = body.data || {};
            setPrereqs(data);

            const p = data.product || {};
            setTitle(p.productName || product.productName || "");
            setDescription(p.productDescription || product.productDescription || "");
            setPrice(p.sellingPrice ? String(p.sellingPrice) : "");
            setQuantity(p.quantity ? String(p.quantity) : "1");
            setCondition(p.condition || p.productCondition || "New");
            setCategoryId(data.suggestedCategory?.categoryId || "");
            setFulfillmentPolicyId(data.policies?.fulfillment?.[0]?.id || "");
            setPaymentPolicyId(data.policies?.payment?.[0]?.id || "");
            setReturnPolicyId(data.policies?.return?.[0]?.id || "");
            setMerchantLocationKey(data.locations?.[0]?.key || "");

            const listings = existingResp?.data?.EntityList || [];
            const existingEbay = listings.find((l) => String(l.marketplaceName).toLowerCase() === "ebay");
            if (existingEbay) setExistingListing(existingEbay);
        } catch (err) {
            if (!isCancelled()) setLoadError(err?.response?.data?.error?.message || err.message || "Failed to load eBay listing fields.");
        } finally {
            if (!isCancelled()) setLoading(false);
        }
    };

    useEffect(() => {
        if (!productId) return undefined;
        const cancelledRef = { current: false };
        loadPrereqs(cancelledRef);
        return () => { cancelledRef.current = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    /**
     * One-time onboarding: create the seller's eBay inventory location.
     * eBay's Inventory API requires a merchantLocationKey on every offer but
     * doesn't expose location management in Seller Hub for most sellers —
     * so we collect the address here and call the location/create endpoint.
     * On success, re-run loadPrereqs() so the listing form replaces this one.
     */
    const handleCreateLocation = async () => {
        if (!locAddressLine1.trim()) { toast.error("Street address is required."); return; }
        if (!locCity.trim()) { toast.error("City is required."); return; }
        if (!locState.trim()) { toast.error("State / province is required."); return; }
        if (!locPostalCode.trim()) { toast.error("Postal code is required."); return; }
        if (!locCountry.trim()) { toast.error("Country is required."); return; }

        setLocSubmitting(true);
        try {
            const resp = await createMarketplaceLocation(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                locationKey: "wh-1",
                name: locName.trim() || "Main Warehouse",
                address: {
                    addressLine1: locAddressLine1.trim(),
                    addressLine2: locAddressLine2.trim() || undefined,
                    city: locCity.trim(),
                    stateOrProvince: locState.trim(),
                    postalCode: locPostalCode.trim(),
                    country: locCountry.trim().toUpperCase(),
                },
            });

            const body = resp?.data;
            if (body?.success !== true || body?.data?.success !== true) {
                const msg = body?.data?.lastError || body?.error?.message || "Failed to register eBay location.";
                toast.error(msg);
                return;
            }
            toast.success("eBay location registered.");
            // Re-load prereqs — the form below this step will now render.
            // User-triggered reload — no cancellation needed (component is mounted by definition)
            await loadPrereqs();
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || err.message || "Failed to register eBay location.");
        } finally {
            setLocSubmitting(false);
        }
    };

    const allAspects = useMemo(() => {
        if (!prereqs) return [];
        return [
            ...(prereqs.requiredFields || []),
            ...(prereqs.recommendedFields || []),
            ...(prereqs.optionalFields || []),
        ];
    }, [prereqs]);

    const handleAspectChange = (name, value) => {
        setAspectValues((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        if (!title.trim()) { toast.error("Title is required."); return false; }
        if (!price || Number.parseFloat(price) <= 0) { toast.error("Enter a valid price."); return false; }
        if (!quantity || Number.parseInt(quantity, 10) <= 0) { toast.error("Enter a valid quantity."); return false; }
        if (!categoryId) { toast.error("Select a category."); return false; }
        if (!fulfillmentPolicyId) { toast.error("Select a fulfillment policy."); return false; }
        if (!paymentPolicyId) { toast.error("Select a payment policy."); return false; }
        if (!returnPolicyId) { toast.error("Select a return policy."); return false; }
        if (!merchantLocationKey) { toast.error("Select a warehouse location."); return false; }
        for (const a of (prereqs?.requiredFields || [])) {
            const v = aspectValues[a.name];
            if (v === undefined || v === null || String(v).trim() === "") {
                toast.error(`${a.name} is required.`);
                return false;
            }
        }
        return true;
    };

    const handlePublish = async () => {
        if (!validate()) return;

        setSubmitting(true);
        try {
            const p = prereqs.product || {};
            const sku = p.eBaySku || p.productSku || product.productSku;
            const imageUrls = (p.images || [])
                .filter((img) => !img.isDeleted)
                .map((img) => img.imageUrl)
                .filter(Boolean);
            if (!imageUrls.length && (product.imageUrl || p.imageUrl)) {
                imageUrls.push(product.imageUrl || p.imageUrl);
            }

            const payload = {
                sku,
                title: title.trim(),
                description,
                condition,
                price: Number.parseFloat(price),
                currency: prereqs.marketplaceMeta?.currency || "USD",
                quantity: Number.parseInt(quantity, 10),
                categoryId,
                fulfillmentPolicyId,
                paymentPolicyId,
                returnPolicyId,
                merchantLocationKey,
                imageUrls,
                brand: p.brand || null,
                mpn: p.mpn || null,
                upc: p.productUpc || p.upc || null,
                ean: p.ean || null,
                weight: p.weight ? Number.parseFloat(p.weight) : null,
                // p.weightUnit comes from .NET as a numeric enum (WMSUnitSet:
                // 1=LB 2=OZ 3=KG 4=GM) — map to eBay's enum.
                weightUnit: mapWeightUnit(p.weightUnit),
                dimensions: (p.length && p.width && p.height) ? {
                    length: Number.parseFloat(p.length),
                    width: Number.parseFloat(p.width),
                    height: Number.parseFloat(p.height),
                    // .NET serializes this field as "DimensionUnit" (PascalCase), not "dimensionUnit".
                    // Numeric enum (WmsDimensionUnit: 1=INCH 2=CMS) — map to eBay's enum.
                    unit: mapDimensionUnit(p.DimensionUnit ?? p.dimensionUnit),
                } : null,
                aspects: buildAspectsPayload(aspectValues, allAspects),
                existingListing: existingListing ? {
                    externalListingId: existingListing.externalListingId,
                    externalOfferId: existingListing.externalOfferId,
                    listingStatus: existingListing.listingStatus,
                    listingUrl: existingListing.listingUrl,
                } : null,
            };

            const resp = await publishMarketplaceListing(EBAY_MARKETPLACE, {
                companySellerId: product.companySellerId,
                inventoryProductId: productId,
                marketplaceId,
                payload,
            });

            const body = resp?.data;
            if (body?.success !== true) {
                toast.error(body?.error?.message || "Failed to publish to eBay.");
                onPublishComplete?.({ success: false, lastError: body?.error?.message });
                return;
            }
            const result = body.data || {};
            if (result.success) {
                setPublishResult(result);
                toast.success(existingListing ? "Listing updated on eBay." : "Listing published to eBay.");
                onPublishComplete?.(result);
            } else {
                toast.error(result.lastError || "eBay rejected the listing.");
                onPublishComplete?.(result);
            }
        } catch (err) {
            const msg = err?.response?.data?.error?.message || err.message || "Failed to publish to eBay.";
            toast.error(msg);
            onPublishComplete?.({ success: false, lastError: msg });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <output className="spinner-border text-primary" aria-hidden="true" />
                <p className="mt-3 text-muted">Fetching listing fields from eBay…</p>
            </div>
        );
    }

    if (missingLocations) {
        return (
            <div className="modal_container_rate_card fontFix">
                <div className="alert alert-info">
                    <strong>One-time setup: register your warehouse address.</strong>
                    <p className="mb-0 mt-2" style={{ fontSize: 13 }}>
                        eBay requires a registered warehouse address before publishing listings.
                        This is a one-time setup per seller — once saved, all future eBay listings
                        will use the same location.
                    </p>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Warehouse Name:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locName}
                            onChange={(e) => setLocName(e.target.value)}
                            placeholder="Main Warehouse"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Street Address:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locAddressLine1}
                            onChange={(e) => setLocAddressLine1(e.target.value)}
                            placeholder="e.g., 123 Main Street"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Address Line 2:</span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locAddressLine2}
                            onChange={(e) => setLocAddressLine2(e.target.value)}
                            placeholder="Apt, suite, unit (optional)"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">City:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locCity}
                            onChange={(e) => setLocCity(e.target.value)}
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">State / Province:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locState}
                            onChange={(e) => setLocState(e.target.value)}
                            placeholder="e.g., NY (2-letter for US)"
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Postal Code:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locPostalCode}
                            onChange={(e) => setLocPostalCode(e.target.value)}
                        />
                    </div>
                </div>

                <div className="row modal_input">
                    <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                        <span className="input-label fnt-wgt-editmgmt">Country:<span style={{ color: "red" }}> *</span></span>
                    </div>
                    <div className="col-lg-8 col-sm-12">
                        <input
                            type="text"
                            className="form-control availableBalanceInput"
                            value={locCountry}
                            onChange={(e) => setLocCountry(e.target.value)}
                            placeholder="2-letter ISO code (US, GB, DE, …)"
                            maxLength={2}
                            style={{ textTransform: "uppercase" }}
                        />
                    </div>
                </div>

                <p className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
                    Note: the address cannot be changed after creation — eBay locks it once registered.
                    Use your real shipping origin. The name can be edited later.
                </p>

                <div className="text-right" style={{ marginTop: 16 }}>
                    <Button
                        className="cus-primary-transparent-btn"
                        onClick={onSkip}
                        disabled={locSubmitting}
                    >
                        Skip eBay
                    </Button>
                    <Button
                        className="cus-seconday-bg-btn marginLeft5"
                        onClick={handleCreateLocation}
                        disabled={locSubmitting}
                    >
                        {locSubmitting ? "Registering…" : "Save Location"}
                    </Button>
                </div>
            </div>
        );
    }

    if (policiesOptedOut) {
        return (
            <div className="alert alert-warning">
                <strong>Opt in to eBay business policies first.</strong>
                <p className="mb-2 mt-2">{policiesOptedOut}</p>
                <p className="mb-2">
                    eBay&apos;s newer business-policies model isn&apos;t enabled on this account.
                    Without it, listings can&apos;t reference fulfillment / payment / return policies.
                </p>
                <p className="mb-2">
                    Open the{" "}
                    <a href={POLICY_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub
                    </a>
                    {" "}→ <em>Account Settings</em> → <em>Business Policies</em> in the left nav, and click <em>Opt in</em>.
                </p>
                <p className="mb-2" style={{ fontSize: 12, color: "#666" }}>
                    Alternate direct link:{" "}
                    <a href={POLICY_OPTIN_URL} target="_blank" rel="noreferrer">
                        bizpolicy.ebay.com — opt in
                    </a>
                    {" "}(may be intermittently unavailable; try Seller Hub above if it fails).
                </p>
                <p className="mb-2">
                    After opting in, create at least one fulfillment, payment, and return policy. Then come back and try again.
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }

    if (missingPolicies) {
        return (
            <div className="alert alert-warning">
                <strong>Set up your eBay business policies first.</strong>
                <p className="mb-2 mt-2">{missingPolicies}</p>
                <p className="mb-2">
                    Open the{" "}
                    <a href={POLICY_HUB_URL} target="_blank" rel="noreferrer">
                        eBay Seller Hub
                    </a>
                    {" "}→ <em>Account Settings</em> → <em>Business Policies</em> to create a fulfillment,
                    payment, and return policy. Then come back and try again.
                </p>
                <Button className="cus-primary-transparent-btn mt-2" onClick={onSkip}>
                    Skip eBay
                </Button>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="alert alert-danger">
                {loadError}
                <div className="mt-2">
                    <Button className="cus-primary-transparent-btn" onClick={onSkip}>Skip eBay</Button>
                </div>
            </div>
        );
    }

    if (publishResult) {
        return (
            <div className="alert alert-success">
                <strong>
                    {existingListing ? "Listing updated on eBay!" : "Listing published to eBay!"}
                </strong>
                {publishResult.listingUrl && (
                    <p className="mb-0 mt-2">
                        <a href={publishResult.listingUrl} target="_blank" rel="noreferrer">
                            View on eBay →
                        </a>
                    </p>
                )}
            </div>
        );
    }

    if (!prereqs) return null;

    // Helper: resolve the currently-selected category object across both lists
    const allCategories = [
        prereqs.suggestedCategory,
        ...(prereqs.otherCategorySuggestions || []),
    ].filter(Boolean);
    const selectedCategory = allCategories.find((c) => c.categoryId === categoryId);

    return (
        <div className="modal_container_rate_card fontFix">
            <div className="row modal_input">
                <div className="col-12 mobMb-10">
                    <p className="mb-3" style={{ fontSize: "13px", color: "#666" }}>
                        <strong>{prereqs.product?.productSku || product.productSku}</strong>
                        {` — ${prereqs.product?.productName || product.productName || ""}`}
                        {existingListing && (
                            <span className="badge badge-info ml-2">Existing listing — Update mode</span>
                        )}
                    </p>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Title:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <input
                        type="text"
                        className="form-control availableBalanceInput"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Description:</span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <textarea
                        className="form-control availableBalanceInput"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">
                        Price ({prereqs.marketplaceMeta?.currency || "USD"}):<span style={{ color: "red" }}> *</span>
                    </span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control availableBalanceInput"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Quantity:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <input
                        type="number"
                        min="1"
                        className="form-control availableBalanceInput"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                    />
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Condition:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={CONDITION_OPTIONS}
                            value={toOption(condition, CONDITION_OPTIONS)}
                            onChange={(opt) => setCondition(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select condition..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Category:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={allCategories.map(optionFromCategory)}
                            value={optionFromCategory(selectedCategory)}
                            onChange={(opt) => setCategoryId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select category..."
                        />
                    </div>
                    <p className="text-muted" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                        Required eBay fields below depend on this category. Changing the category may require different fields — re-open the modal to refresh.
                    </p>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Fulfillment Policy:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.policies?.fulfillment || []).map(optionFromIdName)}
                            value={optionFromIdName((prereqs.policies?.fulfillment || []).find((p) => p.id === fulfillmentPolicyId))}
                            onChange={(opt) => setFulfillmentPolicyId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select policy..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Payment Policy:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.policies?.payment || []).map(optionFromIdName)}
                            value={optionFromIdName((prereqs.policies?.payment || []).find((p) => p.id === paymentPolicyId))}
                            onChange={(opt) => setPaymentPolicyId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select policy..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Return Policy:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.policies?.return || []).map(optionFromIdName)}
                            value={optionFromIdName((prereqs.policies?.return || []).find((p) => p.id === returnPolicyId))}
                            onChange={(opt) => setReturnPolicyId(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select policy..."
                        />
                    </div>
                </div>
            </div>

            <div className="row modal_input">
                <div className="col-lg-4 col-sm-12 mobMb-10 formLabel">
                    <span className="input-label fnt-wgt-editmgmt">Warehouse Location:<span style={{ color: "red" }}> *</span></span>
                </div>
                <div className="col-lg-8 col-sm-12">
                    <div className="select_react">
                        <Select
                            options={(prereqs.locations || []).map(optionFromLocation)}
                            value={optionFromLocation((prereqs.locations || []).find((l) => l.key === merchantLocationKey))}
                            onChange={(opt) => setMerchantLocationKey(opt ? opt.value : "")}
                            className="activeSellerListInput"
                            classNamePrefix="list"
                            placeholder="Select warehouse..."
                        />
                    </div>
                </div>
            </div>

            {prereqs.requiredFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        Required Fields ({prereqs.requiredFields.length})
                    </legend>
                    {prereqs.requiredFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>
                                {aspect.name} <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            {prereqs.recommendedFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                            onClick={() => setShowRecommended((v) => !v)}
                            aria-expanded={showRecommended}
                        >
                            <i className={`fa fa-chevron-${showRecommended ? "down" : "right"} mr-2`} />
                            Recommended Fields ({prereqs.recommendedFields.length})
                        </button>
                    </legend>
                    {showRecommended && prereqs.recommendedFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>{aspect.name}</Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            {prereqs.optionalFields?.length > 0 && (
                <fieldset className="mb-3">
                    <legend style={{ fontSize: 14, fontWeight: 600 }}>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                            onClick={() => setShowOptional((v) => !v)}
                            aria-expanded={showOptional}
                        >
                            <i className={`fa fa-chevron-${showOptional ? "down" : "right"} mr-2`} />
                            Optional Fields ({prereqs.optionalFields.length})
                        </button>
                    </legend>
                    {showOptional && prereqs.optionalFields.map((aspect) => (
                        <Form.Group key={aspect.name} className="mb-2">
                            <Form.Label>{aspect.name}</Form.Label>
                            <AspectField
                                aspect={aspect}
                                value={aspectValues[aspect.name]}
                                onChange={(v) => handleAspectChange(aspect.name, v)}
                            />
                        </Form.Group>
                    ))}
                </fieldset>
            )}

            <p style={{ fontSize: 12, color: "#888", marginTop: 16 }}>
                Publishing creates a billable listing on eBay. Insertion fees may apply depending on your store tier.
            </p>

            <div className="text-right">
                <Button
                    className="cus-seconday-bg-btn"
                    onClick={handlePublish}
                    disabled={submitting}
                >
                    {publishButtonLabel(submitting, !!existingListing)}
                </Button>
            </div>
        </div>
    );
}

EbayStep.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        productSku: PropTypes.string,
        productName: PropTypes.string,
        productDescription: PropTypes.string,
        imageUrl: PropTypes.string,
        companySellerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    marketplaceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onPublishComplete: PropTypes.func,
    onSkip: PropTypes.func,
};
EbayStep.defaultProps = { onPublishComplete: undefined, onSkip: undefined };
